import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Plus,
  ArrowLeft,
  Edit,
  Trash2,
  X,
  Loader2,
  Check,
  Settings,
  Search,
  ChevronsUpDown,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DieIDManagement = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [dieIds, setDieIds] = useState([]);
  const [saving, setSaving] = useState(false);
  const [searchFilter, setSearchFilter] = useState("");

  // Dialog state
  const [showDialog, setShowDialog] = useState(false);
  const [editingDie, setEditingDie] = useState(null);
  const [formData, setFormData] = useState({
    die_id: "",
    description: "",
    company_name: "",
    reel_size: "",
    cutting_size: "",
    status: "Available",
    used_in_product_id: "",
  });

  // Company dropdown state
  const [companies, setCompanies] = useState([]);
  const [companyOpen, setCompanyOpen] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      // Sync die statuses first
      await axios.post(`${API}/die-ids/sync-status`).catch(() => {});
      const [dieResponse, customersResponse] = await Promise.all([
        axios.get(`${API}/die-ids`),
        axios.get(`${API}/wo-customers`),
      ]);
      
      setDieIds(dieResponse.data);
      setCompanies(customersResponse.data || []);
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  const handleAddNew = async () => {
    try {
      const nextResponse = await axios.get(`${API}/die-ids/next`);
      setFormData({
        die_id: nextResponse.data.next_die_id,
        description: "",
        company_name: "",
        reel_size: "",
        cutting_size: "",
        status: "Available",
        used_in_product_id: "",
      });
      setEditingDie(null);
      setShowDialog(true);
    } catch (error) {
      toast.error("Failed to generate next Die ID");
    }
  };

  const handleEdit = (die) => {
    setEditingDie(die);
    setFormData({
      die_id: die.die_id,
      description: die.description || "",
      company_name: die.company_name || "",
      reel_size: die.reel_size || "",
      cutting_size: die.cutting_size || "",
      status: die.status || "Available",
      used_in_product_id: die.used_in_product_id || "",
    });
    setShowDialog(true);
  };

  const handleSave = async () => {
    if (!formData.die_id) {
      toast.error("Die ID is required");
      return;
    }

    setSaving(true);
    try {
      if (editingDie) {
        await axios.put(`${API}/die-ids/${editingDie.id}`, formData);
        toast.success("Die ID updated");
      } else {
        await axios.post(`${API}/die-ids`, formData);
        toast.success("Die ID created");
      }
      setShowDialog(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save Die ID");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (die) => {
    if (!window.confirm(`Delete Die ID ${die.die_id}?`)) return;
    
    try {
      await axios.delete(`${API}/die-ids/${die.id}`);
      toast.success("Die ID deleted");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete Die ID");
    }
  };

  // Filter Die IDs based on search
  const filteredDieIds = dieIds.filter((die) => {
    if (!searchFilter) return true;
    const search = searchFilter.toLowerCase();
    return (
      die.die_id?.toLowerCase().includes(search) ||
      die.description?.toLowerCase().includes(search) ||
      die.company_name?.toLowerCase().includes(search)
    );
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[calc(100vh-64px)] bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
        <Loader2 className="h-12 w-12 animate-spin text-cyan-400" />
      </div>
    );
  }

  return (
    <div className="min-h-[calc(100vh-64px)] bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 p-6">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              onClick={() => navigate("/workorder/prod-material")}
              className="text-gray-400 hover:text-white"
            >
              <ArrowLeft className="h-5 w-5 mr-2" />
              Back
            </Button>
            <div className="flex items-center gap-3">
              <Settings className="h-8 w-8 text-cyan-400" />
              <h1 className="text-2xl font-bold text-white">
                Die ID Store
              </h1>
            </div>
          </div>
        </div>

        {/* Die IDs Card */}
        <Card className="bg-slate-800/50 border-slate-700">
          <CardHeader className="border-b border-slate-700 flex flex-row items-center justify-between">
            <CardTitle className="text-lg text-cyan-400">
              Die IDs ({filteredDieIds.length})
            </CardTitle>
            <div className="flex items-center gap-4">
              {/* Search Filter */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  value={searchFilter}
                  onChange={(e) => setSearchFilter(e.target.value)}
                  placeholder="Search Die IDs..."
                  className="bg-slate-700 border-slate-600 text-white pl-10 w-64"
                  data-testid="die-id-search"
                />
              </div>
              <Button
                onClick={handleAddNew}
                className="bg-cyan-500 hover:bg-cyan-600 text-black font-bold"
                data-testid="add-die-id-btn"
              >
                <Plus className="h-4 w-4 mr-2" />
                Add Die ID
              </Button>
            </div>
          </CardHeader>
          <CardContent className="pt-4">
            <Table>
              <TableHeader>
                <TableRow className="border-slate-700">
                  <TableHead className="text-cyan-400">Die ID</TableHead>
                  <TableHead className="text-cyan-400">Company Name</TableHead>
                  <TableHead className="text-cyan-400">Reel Size</TableHead>
                  <TableHead className="text-cyan-400">Cutting Size</TableHead>
                  <TableHead className="text-cyan-400">Description</TableHead>
                  <TableHead className="text-cyan-400">Status</TableHead>
                  <TableHead className="text-cyan-400">Created</TableHead>
                  <TableHead className="text-cyan-400 text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredDieIds.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center text-gray-400 py-8">
                      {searchFilter ? "No matching Die IDs found." : "No Die IDs yet. Click 'Add Die ID' to create one."}
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredDieIds.map((die) => (
                    <TableRow key={die.id} className="border-slate-700">
                      <TableCell className="text-white font-medium">{die.die_id}</TableCell>
                      <TableCell className="text-gray-300">{die.company_name || "-"}</TableCell>
                      <TableCell className="text-gray-300">{die.reel_size || "-"}</TableCell>
                      <TableCell className="text-gray-300">{die.cutting_size || "-"}</TableCell>
                      <TableCell className="text-gray-300">{die.description || "-"}</TableCell>
                      <TableCell>
                        {die.status === "Used" ? (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-amber-500/20 text-amber-400 border border-amber-500/30" data-testid={`status-${die.die_id}`}>
                            Used {die.used_in_product_id ? `(${die.used_in_product_id})` : ""}
                          </span>
                        ) : (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-500/20 text-green-400 border border-green-500/30" data-testid={`status-${die.die_id}`}>
                            Available
                          </span>
                        )}
                      </TableCell>
                      <TableCell className="text-gray-400 text-sm">
                        {new Date(die.created_at).toLocaleDateString()}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleEdit(die)}
                          className="text-yellow-400 hover:text-yellow-300"
                          data-testid={`edit-die-${die.die_id}`}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDelete(die)}
                          className="text-red-400 hover:text-red-300"
                          data-testid={`delete-die-${die.die_id}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>

      {/* Add/Edit Die ID Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-md bg-slate-900 border-slate-700">
          <DialogHeader>
            <DialogTitle className="text-xl text-cyan-400">
              {editingDie ? "Edit Die ID" : "Add New Die ID"}
            </DialogTitle>
            <DialogDescription className="text-gray-400">
              {editingDie ? "Update the Die ID details" : "Create a new Die ID"}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label className="text-gray-300">Die ID</Label>
              <Input
                value={formData.die_id}
                onChange={(e) => setFormData((prev) => ({ ...prev, die_id: e.target.value }))}
                className="bg-slate-800 border-slate-600 text-white"
                placeholder="DID001"
                data-testid="die-id-input"
              />
              <p className="text-xs text-gray-500">
                {editingDie ? "You can modify the Die ID" : "Auto-generated, can be modified"}
              </p>
            </div>

            <div className="space-y-2">
              <Label className="text-gray-300">Company Name</Label>
              <Popover open={companyOpen} onOpenChange={setCompanyOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    role="combobox"
                    aria-expanded={companyOpen}
                    className="w-full justify-between bg-slate-800 border-slate-600 text-white hover:bg-slate-700"
                    data-testid="company-name-dropdown"
                  >
                    {formData.company_name || "Select Company..."}
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[350px] p-0 bg-slate-900 border-slate-700">
                  <Command className="bg-transparent">
                    <CommandInput placeholder="Search company..." className="text-white" />
                    <CommandList>
                      <CommandEmpty>No company found.</CommandEmpty>
                      <CommandGroup className="max-h-60 overflow-auto">
                        {companies.map((company) => (
                          <CommandItem
                            key={company.id}
                            value={company.company_name}
                            onSelect={() => {
                              setFormData((prev) => ({ ...prev, company_name: company.company_name }));
                              setCompanyOpen(false);
                            }}
                            className="text-white hover:bg-slate-700 cursor-pointer"
                          >
                            <Check
                              className={cn(
                                "mr-2 h-4 w-4",
                                formData.company_name === company.company_name ? "opacity-100" : "opacity-0"
                              )}
                            />
                            {company.company_name}
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-gray-300">Reel Size</Label>
                <Input
                  value={formData.reel_size}
                  onChange={(e) => setFormData((prev) => ({ ...prev, reel_size: e.target.value }))}
                  className="bg-slate-800 border-slate-600 text-white"
                  placeholder="e.g., 28.5"
                  data-testid="reel-size-input"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-gray-300">Cutting Size</Label>
                <Input
                  value={formData.cutting_size}
                  onChange={(e) => setFormData((prev) => ({ ...prev, cutting_size: e.target.value }))}
                  className="bg-slate-800 border-slate-600 text-white"
                  placeholder="e.g., 42x36"
                  data-testid="cutting-size-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-gray-300">Description (Optional)</Label>
              <Input
                value={formData.description}
                onChange={(e) => setFormData((prev) => ({ ...prev, description: e.target.value }))}
                className="bg-slate-800 border-slate-600 text-white"
                placeholder="Description"
                data-testid="die-description-input"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-gray-300">Status</Label>
              <select
                value={formData.status}
                onChange={(e) => setFormData((prev) => ({ ...prev, status: e.target.value }))}
                className="w-full h-10 px-3 rounded-md bg-slate-800 border border-slate-600 text-white"
                data-testid="die-status-select"
              >
                <option value="Available">Available</option>
                <option value="Used">Used</option>
              </select>
              {formData.status === "Used" && (
                <Input
                  value={formData.used_in_product_id}
                  onChange={(e) => setFormData((prev) => ({ ...prev, used_in_product_id: e.target.value }))}
                  className="bg-slate-800 border-slate-600 text-white mt-2"
                  placeholder="Product ID (e.g., PROD-001)"
                  data-testid="die-used-product-input"
                />
              )}
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowDialog(false)}
              className="border-slate-600 text-gray-300"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              disabled={saving}
              className="bg-cyan-500 hover:bg-cyan-600 text-black font-bold"
              data-testid="save-die-id-btn"
            >
              {saving ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : editingDie ? (
                "Update"
              ) : (
                "Create"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DieIDManagement;
